import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import {
  BusinessModel,
  isReusablePaymentMethodType,
  LicenseType,
  PaymentMethodType,
  Product,
  ProductCategory,
  ProductCategoryContext,
  ProductEnvironment,
  ProductIdentifier,
  ProductLocalization,
  ProductPersonalizationAddition,
  ProductPersonalizationAdditionAddressRequirements,
  ProductPersonalizationAdditionBirthDateRequirement,
  SerializedProductService,
  TravelTicketRoute,
  WalletPaymentMethodType
} from '@platform-frontend/marketplace';
import { PLATFORM_ENVIRONMENT } from '@platform-frontend/platform';
import { convertToHttpParams } from '@platform-frontend/util';
import { Buffer } from 'buffer';
import { Observable, of } from 'rxjs';
import { switchMap, tap } from 'rxjs/operators';
import { StartCheckoutPreference } from '../flow';
import { CheckWalletSupport, PaymentWalletState } from '../payment-wallet';
import { ProductStateModel } from './model/product-state-model';
import { FetchProduct } from './product.actions';

const DEFAULT_STATE: ProductStateModel = {
  product: null
};

@State<ProductStateModel>({
  name: 'product',
  defaults: DEFAULT_STATE
})
@Injectable()
export class ProductState {
  constructor(private http: HttpClient, private serializedProductService: SerializedProductService) {}

  @Selector()
  static product(state: ProductStateModel): Product | null {
    return state.product;
  }

  @Selector([ProductState.product])
  static productIdentifier(state: ProductStateModel, product: Product | null): ProductIdentifier | null {
    return product?.productIdentifier ?? null;
  }

  @Selector([ProductState.productIdentifier])
  static productId(state: ProductStateModel, productIdentifier: ProductIdentifier | null): string | null {
    return productIdentifier?.productId ?? null;
  }

  @Selector([ProductState.productIdentifier])
  static productEnvironment(state: ProductStateModel, productIdentifier: ProductIdentifier | null): ProductEnvironment {
    return productIdentifier?.environment ?? 'LIVE';
  }

  @Selector([ProductState.productEnvironment])
  static isLive(state: ProductStateModel, productEnvironment: ProductEnvironment): boolean {
    return productEnvironment === 'LIVE';
  }

  @Selector([ProductState.product])
  static productName(state: ProductStateModel, product: Product | null): string | null {
    return product?.productLocalization.productName ?? null;
  }

  @Selector([ProductState.product])
  static vendorName(state: ProductStateModel, product: Product | null): string | null {
    return product?.productLocalization.vendorName ?? null;
  }

  @Selector([ProductState.product])
  static billingAddressRequired(state: ProductStateModel, product: Product | null): boolean {
    return product?.productLocalization.vendorName.includes('TYPO') ?? false;
  }

  @Selector([ProductState.product])
  static licenseTypes(state: ProductStateModel, product: Product | null): LicenseType[] {
    return product?.licenseTypes ?? [];
  }

  @Selector([ProductState.product, PaymentWalletState.enabledWallets])
  static allowedPaymentMethods(state: ProductStateModel, product: Product | null, enabledWallets: WalletPaymentMethodType[]): PaymentMethodType[] {
    return product?.allowedPaymentMethods.filter(m => isReusablePaymentMethodType(m) || enabledWallets.includes(m)) ?? [];
  }

  @Selector([ProductState.allowedPaymentMethods])
  static isWalletPaymentMethodAllowed(
    state: ProductStateModel,
    allowedPaymentMethods: PaymentMethodType[]
  ): (walletPaymentMethodType: WalletPaymentMethodType) => boolean {
    return (walletPaymentMethodType: WalletPaymentMethodType) => allowedPaymentMethods.includes(walletPaymentMethodType);
  }

  @Selector([ProductState.licenseTypes])
  static commercialLicenseTypes(state: ProductStateModel, licenseTypes: LicenseType[]): LicenseType[] {
    return licenseTypes.filter(licenseType => licenseType.kind.type === 'SUBSCRIPTION' || licenseType.kind.type === 'ONE_TIME_PURCHASE') ?? [];
  }

  @Selector([ProductState.licenseTypes])
  static nonCommercialLicenseTypes(state: ProductStateModel, licenseTypes: LicenseType[]): LicenseType[] {
    return licenseTypes.filter(licenseType => licenseType.kind.type !== 'SUBSCRIPTION' && licenseType.kind.type !== 'ONE_TIME_PURCHASE') ?? [];
  }

  @Selector([ProductState.product])
  static isMultipleCheckoutAllowed(state: ProductStateModel, product: Product | null): boolean {
    return product?.multipleActiveLicensesAllowed ?? false;
  }

  @Selector([ProductState.product])
  static fixedFulfillmentCountry(state: ProductStateModel, product: Product | null): boolean {
    return (product?.fulfillmentCountry ?? null) !== null;
  }

  @Selector([ProductState.product])
  static businessModel(state: ProductStateModel, product: Product | null): BusinessModel | null {
    return product?.businessModel ?? null;
  }

  @Selector([ProductState.product])
  static productCategoryContext(state: ProductStateModel, product: Product | null): ProductCategoryContext | null {
    return product?.productCategoryUpdate.context ?? null;
  }

  @Selector([ProductState.productCategoryContext])
  static travelTicketRoute(state: ProductStateModel, productCategoryContext: ProductCategoryContext | null): TravelTicketRoute | null {
    return productCategoryContext?.category === 'TRAVEL_TICKET' ? productCategoryContext.route : null;
  }

  @Selector([ProductState.productCategoryContext])
  static productCategory(state: ProductStateModel, productCategoryContext: ProductCategoryContext | null): ProductCategory | null {
    return productCategoryContext?.category ?? null;
  }

  @Selector([ProductState.product])
  static productVariant(state: ProductStateModel, product: Product | null): string | null {
    return product?.productLocalization.productVariant ?? null;
  }

  @Selector([ProductState.product])
  static productLocalization(state: ProductStateModel, product: Product | null): ProductLocalization | null {
    return product?.productLocalization ?? null;
  }

  @Selector([ProductState.product])
  static personalizationAddition(state: ProductStateModel, product: Product | null): ProductPersonalizationAddition | null {
    return product?.additions.find(addition => addition.type === 'PERSONALIZATION') ?? null;
  }

  @Selector([ProductState.personalizationAddition])
  static mustIncludeUser(state: ProductStateModel, personalizationAddition: ProductPersonalizationAddition | null): boolean {
    return personalizationAddition?.mustIncludeUser ?? false;
  }

  @Selector([ProductState.personalizationAddition])
  static maxNumberOfPersons(state: ProductStateModel, personalizationAddition: ProductPersonalizationAddition | null): number {
    return personalizationAddition?.maxNumberOfPersons ?? 0;
  }

  @Selector([ProductState.personalizationAddition])
  static addressRequirements(
    state: ProductStateModel,
    personalizationAddition: ProductPersonalizationAddition | null
  ): ProductPersonalizationAdditionAddressRequirements | null {
    return personalizationAddition?.addressRequirements ?? null;
  }

  @Selector([ProductState.personalizationAddition])
  static birthDateRequirement(
    state: ProductStateModel,
    personalizationAddition: ProductPersonalizationAddition | null
  ): ProductPersonalizationAdditionBirthDateRequirement {
    return personalizationAddition?.birthDateRequirement ?? 'FIRST_PERSON';
  }

  @Selector([ProductState.personalizationAddition])
  static isPersonalizationRequired(state: ProductStateModel, personalizationAddition: ProductPersonalizationAddition | null): boolean {
    return personalizationAddition !== null;
  }

  private cleanState(ctx: StateContext<ProductStateModel>): void {
    ctx.patchState(DEFAULT_STATE);
  }

  @Action(FetchProduct)
  fetchProduct(ctx: StateContext<ProductStateModel>, { flow }: FetchProduct): Observable<unknown> {
    if (flow.type === 'SIGN_IN') {
      this.cleanState(ctx);
    } else {
      const startCheckoutPreference: StartCheckoutPreference | null = flow.type !== 'ACADEMIC_REGISTRATION' ? flow.startParams.start : null;
      const productIdentifier: ProductIdentifier = flow.startParams.productIdentifier;
      return this.http
        .get<Product<string>>(PLATFORM_ENVIRONMENT.apiUrl + '/product', {
          params: convertToHttpParams({
            ...productIdentifier,
            startCheckoutPreference: startCheckoutPreference ? Buffer.from(JSON.stringify(startCheckoutPreference)).toString('base64') : undefined
          })
        })
        .pipe(
          tap(product => ctx.patchState({ product: this.serializedProductService.deserializeProduct(product) })),
          switchMap(product =>
            ctx.dispatch(new CheckWalletSupport(product.allowedPaymentMethods, ProductState.isLive(ctx.getState(), product.productIdentifier.environment)))
          )
        );
    }
    return of(null);
  }
}
